import React from "react";
import { Badge, Banner, BlockStack, Box, Button, Card, Collapsible, Divider, InlineStack, Text, Tooltip } from "@shopify/polaris";
import { getTierCapabilities, tierIndex, type TierKey } from "@reach/core";

type PlanDetail = {
  name: string;
  tagline: string;
  price: number;
  annualPrice: number;
  description: string;
  features: string[];
  audience: string;
  trial: string | null;
};

type ComparisonSection = {
  title: string;
  rows: Array<{
    feature: string;
    tooltip?: string;
    values: Record<TierKey, boolean | string>;
  }>;
};

type BillingInfo = {
  subscriptionStatus: string;
  planEffectiveUntil: string | null;
  trialEndsAt: string | null;
  compedReason?: string | null;
};

type SubscriptionTabProps = {
  billingCycle: "monthly" | "annual";
  billingInfo: BillingInfo;
  currentTier: TierKey;
  expandedFaq: number | null;
  formatRetentionLabel: (days: number) => string;
  isShopifyStore: boolean;
  onOpenPlanModal: (planId: TierKey) => void;
  onOpenProviderModal: (planId: TierKey) => void;
  planComparisonSections: ComparisonSection[];
  planDetails: Record<TierKey, PlanDetail>;
  setBillingCycle: React.Dispatch<React.SetStateAction<"monthly" | "annual">>;
  setExpandedFaq: React.Dispatch<React.SetStateAction<number | null>>;
  visiblePlanIds: TierKey[];
};

export function SubscriptionTab({
  billingCycle,
  billingInfo,
  currentTier,
  expandedFaq,
  formatRetentionLabel,
  isShopifyStore,
  onOpenPlanModal,
  onOpenProviderModal,
  planComparisonSections,
  planDetails,
  setBillingCycle,
  setExpandedFaq,
  visiblePlanIds,
}: SubscriptionTabProps) {
  const status = billingInfo.subscriptionStatus;
  const effectiveUntil = billingInfo.planEffectiveUntil
    ? new Date(billingInfo.planEffectiveUntil).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;
  const trialEnd = billingInfo.trialEndsAt
    ? new Date(billingInfo.trialEndsAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;
  const visibleComparisonSections = React.useMemo(() => {
    return planComparisonSections
      .map((section) => ({
        ...section,
        rows: section.rows.filter((row) =>
          visiblePlanIds.some((planId) => {
            const value = row.values[planId];
            return value !== false && value !== "—";
          }),
        ),
      }))
      .filter((section) => section.rows.length > 0);
  }, [planComparisonSections, visiblePlanIds]);

  return (
    <BlockStack gap="600">
      {status === "comped" ? (
        <Banner tone="success">
          <Text as="p" variant="bodySm">
            <strong>Complimentary plan active.</strong>
            {billingInfo.compedReason ? ` ${billingInfo.compedReason}` : " This account is on a sponsored plan."}
          </Text>
        </Banner>
      ) : status === "past_due" ? (
        <Banner tone="warning">
          <Text as="p" variant="bodySm">
            <strong>Payment failed.</strong> Please update your payment method to avoid losing access.
          </Text>
        </Banner>
      ) : status === "cancelled" && effectiveUntil ? (
        <Banner tone="info">
          <Text as="p" variant="bodySm">
            Your subscription is cancelled. You keep access until <strong>{effectiveUntil}</strong>.
          </Text>
        </Banner>
      ) : status === "trialing" && trialEnd ? (
        <Banner tone="info">
          <Text as="p" variant="bodySm">
            Trial active — full access until <strong>{trialEnd}</strong>.
          </Text>
        </Banner>
      ) : null}

      <style>{`
        .theme-subscription-shell { display: flex; flex-direction: column; gap: 24px; }
        .theme-subscription-hero {
          border-radius: 28px;
          border: 1px solid rgba(148,163,184,0.18);
          background:
            radial-gradient(circle at top right, rgba(79,70,229,0.16), transparent 30%),
            radial-gradient(circle at bottom left, rgba(14,165,233,0.1), transparent 26%),
            linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%);
          box-shadow: 0 24px 60px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.85);
          overflow: hidden;
        }
        .theme-subscription-hero-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(300px, 0.85fr);
          gap: 20px;
          align-items: stretch;
        }
        .theme-subscription-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .theme-subscription-summary-chip {
          display: inline-flex;
          flex-direction: column;
          gap: 2px;
          min-width: 132px;
          padding: 12px 14px;
          border-radius: 16px;
          background: rgba(255,255,255,0.76);
          border: 1px solid rgba(226,232,240,0.95);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-subscription-summary-label {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #64748b;
        }
        .theme-subscription-summary-value {
          font-size: 18px;
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #0f172a;
        }
        .theme-subscription-sidecard {
          padding: 18px;
          border-radius: 20px;
          background: rgba(255,255,255,0.8);
          border: 1px solid rgba(226,232,240,0.95);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-subscription-cycle {
          display: inline-flex;
          align-items: center;
          gap: 14px;
          padding: 10px 14px;
          border-radius: 999px;
          background: rgba(248,250,252,0.92);
          border: 1px solid rgba(226,232,240,0.95);
        }
        .theme-subscription-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 18px;
        }
        .theme-subscription-plan {
          position: relative;
          height: 100%;
          border-radius: 24px;
          border: 1px solid rgba(226,232,240,0.95);
          background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%);
          box-shadow: 0 16px 40px rgba(15,23,42,0.05), inset 0 1px 0 rgba(255,255,255,0.9);
          overflow: hidden;
        }
        .theme-subscription-plan[data-active="true"] {
          border-color: rgba(79,70,229,0.34);
          box-shadow: 0 20px 48px rgba(79,70,229,0.12), inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .theme-subscription-plan[data-dimmed="true"] {
          opacity: 0.62;
        }
        .theme-subscription-plan-head {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: flex-start;
        }
        .theme-subscription-plan-audience {
          font-size: 12px;
          line-height: 1.55;
          color: #64748b;
        }
        .theme-subscription-plan-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-height: 152px;
        }
        .theme-subscription-plan-list-item {
          display: flex;
          gap: 10px;
          align-items: flex-start;
        }
        .theme-subscription-plan-dot {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          margin-top: 7px;
          background: linear-gradient(180deg, #4f46e5, #0ea5e9);
          flex-shrink: 0;
        }
        .theme-subscription-action {
          width: 100%;
          border: none;
          border-radius: 14px;
          padding: 12px 14px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 14px 30px rgba(15,23,42,0.16);
        }
        .theme-subscription-action:hover { filter: brightness(1.06); }
        .theme-subscription-action:disabled {
          cursor: default;
          filter: none;
        }
        .theme-subscription-action--muted {
          background: linear-gradient(135deg, #cbd5e1 0%, #e2e8f0 48%, #f8fafc 100%);
          color: #475569;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.72);
        }
        .theme-subscription-action--danger {
          background: linear-gradient(135deg, #7f1d1d 0%, #b91c1c 52%, #ef4444 100%);
          box-shadow: 0 14px 30px rgba(185,28,28,0.24);
        }
        .theme-subscription-compare {
          overflow: hidden;
          border-radius: 24px;
          border: 1px solid rgba(226,232,240,0.95);
          background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%);
        }
        .theme-subscription-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 13px;
        }
        .theme-subscription-table thead th {
          padding: 14px 16px;
          background: rgba(248,250,252,0.9);
          color: #475569;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          border-bottom: 1px solid rgba(226,232,240,0.95);
        }
        .theme-subscription-table thead th[data-current="true"] {
          background: linear-gradient(180deg, rgba(238,242,255,0.98) 0%, rgba(224,231,255,0.88) 100%);
          color: #3730a3;
        }
        .theme-subscription-section-row td {
          padding: 10px 16px;
          background: rgba(241,245,249,0.9);
          color: #334155;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .theme-subscription-table tbody td {
          padding: 12px 16px;
          border-bottom: 1px solid rgba(241,245,249,0.95);
          color: #334155;
          vertical-align: middle;
        }
        .theme-subscription-table tbody td[data-current="true"] {
          background: rgba(238,242,255,0.42);
        }
        .theme-subscription-feature-cell {
          min-width: 260px;
          font-weight: 600;
        }
        .theme-subscription-yes {
          color: #166534;
          font-weight: 800;
        }
        .theme-subscription-no {
          color: #94a3b8;
          font-weight: 700;
        }
        .theme-subscription-chip-value {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px 10px;
          border-radius: 999px;
          background: rgba(248,250,252,0.95);
          border: 1px solid rgba(226,232,240,0.95);
          color: #334155;
          font-size: 12px;
          font-weight: 700;
        }
        .theme-subscription-trust {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
        }
        .theme-subscription-trust-card {
          padding: 18px;
          border-radius: 18px;
          border: 1px solid rgba(226,232,240,0.95);
          background: rgba(255,255,255,0.84);
          text-align: center;
        }
        .theme-subscription-kicker {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #6366f1;
        }
        @media (max-width: 960px) {
          .theme-subscription-hero-grid { grid-template-columns: 1fr; }
          .theme-subscription-trust { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="theme-subscription-shell">
        <div className="theme-subscription-hero">
          <Box padding="500">
            <div className="theme-subscription-hero-grid">
              <BlockStack gap="400">
                <BlockStack gap="150">
                  <div className="theme-subscription-kicker">
                    <Text as="p" variant="bodyXs">
                      Subscription Overview
                    </Text>
                  </div>
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="h2" variant="headingXl">
                      {planDetails[currentTier]?.name}
                    </Text>
                    <Badge tone={currentTier === "free" ? undefined : "success"}>{currentTier === "free" ? "Free" : "Active plan"}</Badge>
                  </InlineStack>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {planDetails[currentTier]?.description}
                  </Text>
                </BlockStack>
                <div className="theme-subscription-summary">
                  <div className="theme-subscription-summary-chip">
                    <span className="theme-subscription-summary-label">Current Tier</span>
                    <span className="theme-subscription-summary-value">{planDetails[currentTier]?.name}</span>
                  </div>
                  <div className="theme-subscription-summary-chip">
                    <span className="theme-subscription-summary-label">Billing</span>
                    <span className="theme-subscription-summary-value">{billingCycle === "annual" ? "Annual" : "Monthly"}</span>
                  </div>
                  <div className="theme-subscription-summary-chip">
                    <span className="theme-subscription-summary-label">Analytics Retention</span>
                    <span className="theme-subscription-summary-value">{formatRetentionLabel(getTierCapabilities(currentTier).analyticsRetentionDays)}</span>
                  </div>
                  <div className="theme-subscription-summary-chip">
                    <span className="theme-subscription-summary-label">Designed For</span>
                    <span className="theme-subscription-summary-value">
                      {currentTier === "free" ? "Getting started" : currentTier === "growth" ? "Brand + ROI" : currentTier === "agent" ? "Automation" : currentTier === "rep" ? "Sales workflows" : "Unlimited scale"}
                    </span>
                  </div>
                </div>
              </BlockStack>

              <div className="theme-subscription-sidecard">
                <BlockStack gap="300">
                  <Text as="p" variant="bodySm" fontWeight="semibold">
                    Billing cycle
                  </Text>
                  <div className="theme-subscription-cycle">
                    <Text as="span" variant="bodySm" fontWeight={billingCycle === "monthly" ? "bold" : "regular"}>
                      Monthly
                    </Text>
                    <button
                      onClick={() => setBillingCycle((prev) => (prev === "monthly" ? "annual" : "monthly"))}
                      className="theme-billing-cycle-toggle"
                      data-annual={billingCycle === "annual"}
                    >
                      <div className="theme-billing-cycle-thumb" data-annual={billingCycle === "annual"} />
                    </button>
                    <InlineStack gap="100" blockAlign="center">
                      <Text as="span" variant="bodySm" fontWeight={billingCycle === "annual" ? "bold" : "regular"}>
                        Annual
                      </Text>
                      <Badge tone="success">Save 17%</Badge>
                    </InlineStack>
                  </div>
                </BlockStack>
              </div>
            </div>
          </Box>
        </div>

        <div className="theme-subscription-grid">
          {visiblePlanIds.map((planId) => {
            const plan = planDetails[planId];
            const isFreeCard = planId === "free";
            const isActive = currentTier === planId;
            const isGrayedOut = isFreeCard && currentTier !== "free";
            const displayPrice = billingCycle === "annual" ? plan.annualPrice : plan.price;
            const isUpgrade = tierIndex(planId) > tierIndex(currentTier);

            return (
              <div key={planId} className="theme-subscription-plan" data-active={isActive ? "true" : "false"} data-dimmed={isGrayedOut ? "true" : "false"}>
                <Box padding="500">
                  <BlockStack gap="400">
                    <div className="theme-subscription-plan-head">
                      <BlockStack gap="100">
                        <Text variant="headingMd" as="h3">
                          {plan.name}
                        </Text>
                        <Text variant="bodyXs" as="p" tone="subdued" fontWeight="bold">
                          {plan.tagline}
                        </Text>
                      </BlockStack>
                      {isActive && <Badge tone="success">Active</Badge>}
                    </div>

                    <BlockStack gap="100">
                      <InlineStack gap="100" blockAlign="baseline">
                        <Text variant="heading2xl" as="h3">
                          ${displayPrice}
                        </Text>
                        {!isFreeCard && (
                          <Text variant="bodySm" as="span" tone="subdued">
                            /mo
                          </Text>
                        )}
                      </InlineStack>
                      {billingCycle === "annual" && !isFreeCard && (
                        <Text variant="bodyXs" as="p" tone="success">
                          Save ${(plan.price - plan.annualPrice) * 12}/year
                        </Text>
                      )}
                    </BlockStack>

                    <Text variant="bodySm" as="p" tone="subdued">
                      {plan.description}
                    </Text>
                    <div className="theme-subscription-plan-audience">
                      <Text variant="bodyXs" as="p">
                        {plan.audience}
                      </Text>
                    </div>

                    <div className="theme-subscription-plan-list">
                      {plan.features.map((feature) => (
                        <div key={feature} className="theme-subscription-plan-list-item">
                          <div className="theme-subscription-plan-dot" />
                          <Text variant="bodySm" as="p" tone="subdued">
                            {feature}
                          </Text>
                        </div>
                      ))}
                    </div>

                    {isFreeCard ? (
                      <button className="theme-subscription-action theme-subscription-action--muted" disabled>
                        {currentTier === "free" ? "Current Plan" : "Free Forever"}
                      </button>
                    ) : !isShopifyStore ? (
                      billingInfo.subscriptionStatus === "comped" ? (
                        <Button fullWidth disabled>
                          Sponsored Plan
                        </Button>
                      ) : isActive ? (
                        <button className="theme-subscription-action theme-subscription-action--danger" onClick={() => onOpenPlanModal("free")}>
                          Cancel Subscription
                        </button>
                      ) : (
                        <button className="theme-subscription-action" onClick={() => onOpenProviderModal(planId)}>
                          {isUpgrade ? "Upgrade" : "Downgrade"}
                        </button>
                      )
                    ) : isActive ? (
                      <button className="theme-subscription-action theme-subscription-action--danger" onClick={() => onOpenPlanModal("free")}>
                        Cancel Subscription
                      </button>
                    ) : (
                      <button className="theme-subscription-action" onClick={() => onOpenPlanModal(planId)}>
                        {isUpgrade ? "Upgrade" : "Downgrade"}
                      </button>
                    )}
                  </BlockStack>
                </Box>
              </div>
            );
          })}
        </div>

        <div className="theme-subscription-compare">
          <Box padding="500">
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Feature Comparison
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  This matrix reflects the actual tier gates used in the product today, not an outdated marketing snapshot.
                </Text>
              </BlockStack>
              <div style={{ overflowX: "auto" }}>
                <table className="theme-subscription-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Feature</th>
                      {visiblePlanIds.map((planId) => (
                        <th key={planId} style={{ textAlign: "center" }} data-current={currentTier === planId ? "true" : "false"}>
                          {planDetails[planId].name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleComparisonSections.map((section) => (
                      <React.Fragment key={section.title}>
                        <tr className="theme-subscription-section-row">
                          <td colSpan={visiblePlanIds.length + 1}>{section.title}</td>
                        </tr>
                        {section.rows.map((row) => (
                          <tr key={row.feature}>
                            <td className="theme-subscription-feature-cell">
                              {row.tooltip ? (
                                <Tooltip content={row.tooltip}>
                                  <span className="theme-tooltip-underline">{row.feature}</span>
                                </Tooltip>
                              ) : (
                                row.feature
                              )}
                            </td>
                            {visiblePlanIds.map((planId) => {
                              const value = row.values[planId];
                              return (
                                <td key={planId} style={{ textAlign: "center" }} data-current={currentTier === planId ? "true" : "false"}>
                                  {typeof value === "boolean" ? value ? <span className="theme-subscription-yes">&#10003;</span> : <span className="theme-subscription-no">&#8212;</span> : <span className="theme-subscription-chip-value">{value}</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </BlockStack>
          </Box>
        </div>

        <div className="theme-subscription-trust">
          <div className="theme-subscription-trust-card">
            <BlockStack gap="100" align="center">
              <Text as="p" variant="bodySm" fontWeight="bold" alignment="center">
                Secure Checkout
              </Text>
              <Text as="p" variant="bodyXs" tone="subdued" alignment="center">
                Shopify Billing, Stripe, and Lemon Squeezy flows stay intact.
              </Text>
            </BlockStack>
          </div>
          <div className="theme-subscription-trust-card">
            <BlockStack gap="100" align="center">
              <Text as="p" variant="bodySm" fontWeight="bold" alignment="center">
                Change Plans Anytime
              </Text>
              <Text as="p" variant="bodyXs" tone="subdued" alignment="center">
                Upgrades are immediate. Cancellations and downgrades follow billing-cycle rules.
              </Text>
            </BlockStack>
          </div>
          <div className="theme-subscription-trust-card">
            <BlockStack gap="100" align="center">
              <Text as="p" variant="bodySm" fontWeight="bold" alignment="center">
                Clear Feature Access
              </Text>
              <Text as="p" variant="bodyXs" tone="subdued" alignment="center">
                See exactly what each plan includes before you switch.
              </Text>
            </BlockStack>
          </div>
        </div>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Frequently Asked Questions
            </Text>
            {([
              { q: "Can I change plans at any time?", a: "Yes. Upgrades are immediate and may be prorated by the billing provider. Downgrades and cancellations take effect at the end of the current billing period." },
              { q: "What happens to my data if I downgrade?", a: "Your store returns to the lower tier gates immediately or at period end depending on the billing flow. Analytics retention and chatbot capabilities follow the destination plan." },
              { q: "Is there a discount for annual billing?", a: "Yes. Annual billing lowers the effective monthly rate across paid tiers and keeps the same feature access." },
            ] as Array<{ q: string; a: string }>).map((faq, index, array) => (
              <div key={index}>
                <button onClick={() => setExpandedFaq(expandedFaq === index ? null : index)} className="theme-faq-toggle">
                  <Text as="span" variant="bodySm" fontWeight="bold">
                    {faq.q}
                  </Text>
                  <span className="theme-faq-plus" style={{ transform: expandedFaq === index ? "rotate(45deg)" : "none" }}>
                    +
                  </span>
                </button>
                <Collapsible open={expandedFaq === index} id={`faq-${index}`}>
                  <Box padding="200" paddingBlockEnd="400">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {faq.a}
                    </Text>
                  </Box>
                </Collapsible>
                {index < array.length - 1 && <Divider />}
              </div>
            ))}
          </BlockStack>
        </Card>
      </div>
    </BlockStack>
  );
}
